/**
 * Parsing helpers for MEDIS v2 responses.
 *
 * The v2 API (Azure Function behind EasyAuth) returns ONLY the inner `Response`
 * body of the legacy `{ State, Response }` envelope, and its keys are PascalCase
 * (`Patient`, `FhirID`, `ConsID`, `AccessListDetails`, …) — different from the
 * v1 API (full envelope, other casing). These helpers make the v2 mappers
 * tolerant of both shapes: they unwrap an envelope if one is present and read
 * keys case-insensitively, so a future casing tweak on the server can't quietly
 * empty the lists again.
 */

/** Unwrap a `{ State, Response }` envelope if present; otherwise return as-is. */
export function unwrapV2<T = unknown>(data: unknown): T {
  if (data && typeof data === 'object' && 'Response' in (data as Record<string, unknown>)) {
    return ((data as Record<string, unknown>).Response ?? {}) as T;
  }
  return (data ?? {}) as T;
}

/** Case-insensitive property read — PascalCase / camelCase / lowercase all match. */
export function prop<T = unknown>(obj: unknown, key: string): T | undefined {
  if (!obj || typeof obj !== 'object') return undefined;
  const rec = obj as Record<string, unknown>;
  if (key in rec) return rec[key] as T;
  const lower = key.toLowerCase();
  for (const k of Object.keys(rec)) {
    if (k.toLowerCase() === lower) return rec[k] as T;
  }
  return undefined;
}

/** Coerce a possibly-null / single value into an array (never throws). */
export function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

/** Parse an ISO date string into a Date, or null when missing/invalid. */
export function parseV2Date(value?: string | null): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Dev-only: log the top-level keys of a v2 payload so the real wire casing is
 * visible in the browser console while the migration settles. No-op in prod.
 */
export function debugV2Shape(label: string, body: unknown): void {
  if (import.meta.env.DEV && body && typeof body === 'object') {
    console.debug(`[v2:${label}] keys:`, Object.keys(body as Record<string, unknown>));
  }
}
